import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import FilterMenuItem from "./FilterMenuItem";
import Text from "@/components/ui/text";
import { Check } from "lucide-react";
import { BooleanFilterValue, BOOLEAN_FILTER_TRANSLATION_KEY } from "./types";

interface Props {
  filterOption: DataTableFilter;
  filters: Filters;
  setFilters: FiltersSetter;
  onClose?: () => void;
}

const FilterBooleanSubmenu: React.FC<Props> = ({
  filterOption,
  filters,
  setFilters,
  onClose
}) => {
  const { t } = useTranslation();
  const filterKey = filterOption.key;
  const currentValue = filters[filterKey as keyof Filters] as
    | BooleanFilterValue
    | undefined;

  const trueLabelKey =
    filterOption.filterType.type === "boolean"
      ? filterOption.filterType.trueLabel
      : undefined;
  const falseLabelKey =
    filterOption.filterType.type === "boolean"
      ? filterOption.filterType.falseLabel
      : undefined;

  const options = [
    {
      value: true,
      label: t(trueLabelKey ?? `${BOOLEAN_FILTER_TRANSLATION_KEY}.true`)
    },
    {
      value: false,
      label: t(falseLabelKey ?? `${BOOLEAN_FILTER_TRANSLATION_KEY}.false`)
    }
  ];

  const selectedValue = useMemo(() => {
    if (currentValue?.eq === true) return true;
    if (currentValue?.eq === false) return false;
    return null;
  }, [currentValue]);

  const handleSelect = (value: boolean) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (selectedValue === value) {
        delete newFilters[filterKey];
      } else {
        newFilters[filterKey] = { eq: value };
      }
      return newFilters;
    });
    onClose?.();
  };

  return (
    <div className="flex flex-col">
      {options.map((option) => {
        const isSelected = selectedValue === option.value;
        return (
          <FilterMenuItem
            key={String(option.value)}
            onClick={() => handleSelect(option.value)}
            isActive={isSelected}
          >
            <div className="flex items-center justify-between w-full">
              <Text variant="body">{option.label}</Text>
              {isSelected && <Check className="h-4 w-4 text-primary" />}
            </div>
          </FilterMenuItem>
        );
      })}
    </div>
  );
};

export default FilterBooleanSubmenu;
